import { useEffect, useState } from 'react';
import { Button, Select, Spinner, makeStyles } from '@fluentui/react-components';

import { adminSetTenantPlan, type SimulatedPlan } from '../api/admin.js';
import { useAuthStore } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';
import { AdminUsageSimulator } from './AdminUsageSimulator.js';

const useStyles = makeStyles({
  panel: {
    background: 'var(--bg-surface)',
    border: '1px dashed var(--border-strong)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 20px',
  },
  eyebrow: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.1px',
    textTransform: 'uppercase',
    color: 'var(--warn)',
  },
  desc: { fontSize: '12.5px', color: 'var(--text-secondary)', margin: '6px 0 14px', lineHeight: 1.45 },
  row: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  result: { fontSize: '12px', color: 'var(--text-secondary)' },
  resultError: { color: 'var(--bad)' },
  divider: {
    marginTop: '18px',
    paddingTop: '14px',
    borderTop: '1px solid var(--border-subtle)',
  },
  subTitle: { fontSize: '13px', fontWeight: 650, color: 'var(--text-primary)', margin: '0 0 4px' },
});

/**
 * Herramientas de prueba SOLO staff Ecosoft (admin/owner del tenant Ecosoft):
 * simular plan del tenant + forzar contadores de consumo (QA). Gatear el
 * render con `useIsEcosoftStaff()` — el cliente nunca debe ver esto.
 */
export function StaffTestingPanel() {
  const styles = useStyles();
  const tenant = useAuthStore((s) => s.tenant);
  const usage = useUsageStore((s) => s.data);
  const refreshUsage = useUsageStore((s) => s.refresh);

  const [planDraft, setPlanDraft] = useState<SimulatedPlan>('start');
  const [planApplying, setPlanApplying] = useState(false);
  const [planResult, setPlanResult] = useState<string | null>(null);

  useEffect(() => {
    if (usage?.plan) setPlanDraft(usage.plan);
  }, [usage?.plan]);

  if (!tenant) return null;

  async function applyPlan() {
    if (!tenant) return;
    setPlanApplying(true);
    setPlanResult(null);
    try {
      const r = await adminSetTenantPlan(tenant.id, planDraft);
      setPlanResult(`Plan actualizado a "${r.simulatedPlan}" para ${tenant.name}.`);
      await refreshUsage();
    } catch (err) {
      setPlanResult(`ERROR: ${(err as Error).message}`);
    } finally {
      setPlanApplying(false);
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.eyebrow}>Pruebas internas · staff Ecosoft</div>
      <p className={styles.desc}>
        Cambia el plan del usuario actual para probar meters y enforcement de cuotas.
        Mecanismo provisional hasta que ADLIC/Anywhere provea el plan real.
      </p>
      <div className={styles.row}>
        <Select
          value={planDraft}
          onChange={(_, d) => setPlanDraft(d.value as SimulatedPlan)}
          disabled={planApplying}
          style={{ minWidth: 180 }}
        >
          <option value="start">Start (3 proy · 30 chat · 500 conc)</option>
          <option value="pro">Pro (10 · 200 · 2 000)</option>
          <option value="teams">Teams (50 · 1 000 · 10 000)</option>
          <option value="vitruvio">Vitruvio (100 · 1 000 · 15 000)</option>
        </Select>
        <Button
          appearance="primary"
          onClick={() => void applyPlan()}
          disabled={planApplying || planDraft === usage?.plan}
          icon={planApplying ? <Spinner size="tiny" /> : undefined}
        >
          {planApplying ? 'Aplicando…' : 'Aplicar'}
        </Button>
        {planResult && (
          <span className={`${styles.result}${planResult.startsWith('ERROR') ? ` ${styles.resultError}` : ''}`}>
            {planResult}
          </span>
        )}
      </div>

      <div className={styles.divider}>
        <h3 className={styles.subTitle}>Forzar estado de consumo (QA)</h3>
        <p className={styles.desc}>
          Presets de un click o counters manuales (ej. <code>chats=29</code> para probar el
          último mensaje permitido). Aplica al tenant actual y refresca la UI al instante.
        </p>
        <AdminUsageSimulator tenantId={tenant.id} tenantName={tenant.name} />
      </div>
    </div>
  );
}
